import { createTheme, alpha } from '@mui/material/styles';

// XTConnect color palette (matching original Admin UI)
const colors = {
    primary: '#0891b2',
    primaryLight: '#22d3ee',
    secondary: '#7c3aed',
    tertiary: '#0d9488',
    success: '#059669',
    warning: '#d97706',
    error: '#dc2626',

    slate: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
        950: '#020617',
    }
};

export const theme = createTheme({
    palette: {
        primary: {
            main: colors.primary,
            light: colors.primaryLight,
            dark: '#0e7490',
            contrastText: '#ffffff',
        },
        secondary: {
            main: colors.tertiary,
            light: '#14b8a6',
            dark: '#0f766e',
            contrastText: '#ffffff',
        },
        error: {
            main: colors.error,
            light: '#ef4444',
            dark: '#b91c1c',
        },
        warning: {
            main: colors.warning,
            light: '#f59e0b',
            dark: '#b45309',
        },
        success: {
            main: colors.success,
            light: '#10b981',
            dark: '#047857',
        },
        background: {
            default: colors.slate[50],
            paper: '#ffffff',
        },
        text: {
            primary: colors.slate[800],
            secondary: colors.slate[500],
        },
        divider: colors.slate[200],
    },
    typography: {
        fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
        fontSize: 14,
        h1: {
            fontWeight: 700,
            letterSpacing: '-0.5px',
        },
        h2: {
            fontWeight: 700,
            letterSpacing: '-0.5px',
        },
        h3: {
            fontWeight: 600,
            letterSpacing: '-0.25px',
        },
        h4: {
            fontWeight: 600,
        },
        h5: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 600,
        },
        button: {
            fontWeight: 500,
            textTransform: 'none',
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.slate[50],
                    WebkitFontSmoothing: 'antialiased',
                },
                '.mono': {
                    fontFamily: '"JetBrains Mono", monospace',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.primary,
                    backgroundImage: `linear-gradient(90deg, ${colors.primary}, ${colors.tertiary})`,
                    boxShadow: `0 4px 12px ${alpha(colors.primary, 0.3)}`,
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    background: `linear-gradient(180deg, ${colors.slate[900]} 0%, ${colors.slate[950]} 100%)`,
                    borderRight: 'none',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    padding: '10px 20px',
                    fontWeight: 500,
                },
                contained: {
                    boxShadow: `0 4px 12px ${alpha(colors.primary, 0.3)}`,
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 6px 16px ${alpha(colors.primary, 0.4)}`,
                    },
                },
                containedPrimary: {
                    backgroundImage: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: `1px solid ${colors.slate[100]}`,
                    boxShadow: 'none',
                    transition: 'all 0.3s',
                    '&:hover': {
                        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)',
                        borderColor: colors.slate[200],
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                rounded: {
                    borderRadius: 16,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: 12,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.slate[300],
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.primary,
                            borderWidth: 1,
                            boxShadow: `0 0 0 3px ${alpha(colors.primary, 0.1)}`,
                        },
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 1,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 9999,
                    fontWeight: 500,
                },
                colorSuccess: {
                    backgroundColor: alpha(colors.success, 0.1),
                    color: colors.success,
                    border: `1px solid ${alpha(colors.success, 0.2)}`,
                },
                colorWarning: {
                    backgroundColor: alpha(colors.warning, 0.1),
                    color: colors.warning,
                    border: `1px solid ${alpha(colors.warning, 0.2)}`,
                },
                colorError: {
                    backgroundColor: alpha(colors.error, 0.1),
                    color: colors.error,
                    border: `1px solid ${alpha(colors.error, 0.2)}`,
                },
                colorInfo: {
                    backgroundColor: alpha(colors.primary, 0.1),
                    color: colors.primary,
                    border: `1px solid ${alpha(colors.primary, 0.2)}`,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        backgroundColor: colors.slate[50],
                        color: colors.slate[500],
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: `1px solid ${colors.slate[100]}`,
                    },
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background 0.2s',
                    '&:hover': {
                        backgroundColor: colors.slate[50],
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: `1px solid ${colors.slate[100]}`,
                    padding: '16px',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    margin: '0 8px 4px',
                    padding: '12px 16px',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(colors.primaryLight, 0.15),
                        '&:hover': {
                            backgroundColor: alpha(colors.primaryLight, 0.2),
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 4,
                            height: 32,
                            background: `linear-gradient(180deg, ${colors.primaryLight}, ${colors.tertiary})`,
                            borderRadius: '0 4px 4px 0',
                        },
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 40,
                    color: colors.slate[400],
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: colors.slate[400],
                    fontWeight: 500,
                    fontSize: '0.875rem',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: colors.slate[800],
                    borderRadius: 8,
                    padding: '8px 14px',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 20,
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                },
            },
        },
    },
});

// Export colors for use in custom components
export { colors };
